import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/util/cn';
import { textOnly, useSanitizedHtmlProps } from '@/util/sanitized';

// Lightweight FAQ-style accordion. Uses the native <details>/<summary>
// element so there is no state machinery, no useEffect, and the open
// state is preserved by the browser. Items render their `title` and
// `description` as sanitized HTML (the parseHTMLtoList helper that
// feeds this component extracts both fields from the API's prose).
//
// All items can be open at once. Pass `defaultOpenFirst` to start with
// the first item expanded (matches the legacy CollapsableList default).

export type FaqItem = {
  title: string;
  description: string;
};

export type FaqListProps = {
  items: FaqItem[];
  defaultOpenFirst?: boolean;
  className?: string;
};

const summaryClass = [
  'flex cursor-pointer list-none items-center justify-between gap-3',
  'px-5 py-3 text-sm font-semibold text-secondary',
  '[&::-webkit-details-marker]:hidden',
].join(' ');

// Titles come in wrapped in <h3> and occasionally have inline emphasis
// (<strong>, <b>) inside. Rendering that HTML caused inconsistent
// weights across items in the same list — some items inherited just
// the summary's font-semibold, others stacked the inner <strong>'s 700
// weight on top. The title is just a question, so strip tags and
// render as plain text; the description keeps its full HTML below.
//
// `textOnly` rather than a tag-stripping regex, because the summary is a
// React text child: entities left encoded print verbatim, so translated
// questions showed `f&uuml;r` instead of `für`.
const stripTags = (html: string) => textOnly(html).trim();

const Row = ({ item, defaultOpen }: { item: FaqItem; defaultOpen?: boolean }) => {
  const descProps = useSanitizedHtmlProps(item.description);
  return (
    <details
      className="group rounded-md border border-border bg-background transition open:bg-muted/30"
      open={defaultOpen}
    >
      <summary className={summaryClass}>
        <span>{stripTags(item.title)}</span>
        <ChevronDown
          className="h-4 w-4 shrink-0 text-muted-foreground transition group-open:rotate-180"
          aria-hidden
        />
      </summary>
      <div className="border-t border-border px-5 py-3 text-sm leading-relaxed text-foreground">
        <div {...descProps} />
      </div>
    </details>
  );
};

export const FaqList = ({ items, defaultOpenFirst, className }: FaqListProps) => {
  if (!items?.length) return null;
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {items.map((item, i) => (
        <Row
          key={item.title}
          item={item}
          defaultOpen={defaultOpenFirst && i === 0}
        />
      ))}
    </div>
  );
};
